import { useCallback } from "react";
import type { BaseEntity, SimpleTableConfig } from "../../types";
import { applyMultiColumnSort } from "../../utils";

interface UseSortCallbacksProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  sortField: string;
  sortColumns: Array<{ field: string; direction: "asc" | "desc"; priority: number }>;
  setSortField: React.Dispatch<React.SetStateAction<string>>;
  setSortDirection: React.Dispatch<React.SetStateAction<"asc" | "desc">>;
  setSortColumns: React.Dispatch<
    React.SetStateAction<Array<{ field: string; direction: "asc" | "desc"; priority: number }>>
  >;
}

export function useSortCallbacks<TData extends BaseEntity>({
  config,
  sortField,
  sortColumns,
  setSortField,
  setSortDirection,
  setSortColumns,
}: UseSortCallbacksProps<TData>) {
  const addSortColumn = useCallback(
    (field: string, direction: "asc" | "desc") => {
      setSortColumns((prev) => {
        const existingIndex = prev.findIndex((col) => col.field === field);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = { field, direction, priority: updated[existingIndex].priority };
          return updated;
        } else {
          const maxPriority = prev.length > 0 ? Math.max(...prev.map((col) => col.priority)) : 0;
          return [...prev, { field, direction, priority: maxPriority + 1 }];
        }
      });
    },
    [setSortColumns]
  );

  const removeSortColumn = useCallback(
    (field: string) => {
      setSortColumns((prev) => prev.filter((col) => col.field !== field));
    },
    [setSortColumns]
  );

  const clearAllSorting = useCallback(() => {
    setSortColumns([]);
  }, [setSortColumns]);

  const handleSort = useCallback(
    (field: string) => {
      if (config.features?.multiColumnSort) {
        const existingSort = sortColumns.find((col) => col.field === field);
        if (existingSort) {
          if (existingSort.direction === "asc") {
            addSortColumn(field, "desc");
          } else {
            removeSortColumn(field);
          }
        } else {
          addSortColumn(field, "asc");
        }
      } else {
        if (sortField === field) {
          setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
          setSortField(field);
          setSortDirection("asc");
        }
      }
    },
    [
      sortField,
      sortColumns,
      addSortColumn,
      removeSortColumn,
      config.features?.multiColumnSort,
      setSortField,
      setSortDirection,
    ]
  );

  const applyMultiColumnSortCallback = useCallback(
    (a: TData, b: TData) => {
      return applyMultiColumnSort(a, b, sortColumns);
    },
    [sortColumns]
  );

  return {
    handleSort,
    addSortColumn,
    removeSortColumn,
    clearAllSorting,
    applyMultiColumnSortCallback,
  };
}
